import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { OnlineProvider } from '@/contexts/OnlineContext';
import { ToastProvider } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { Login } from '@/pages/Login';
import { LockScreen } from '@/pages/LockScreen';
import { Dashboard, DashboardSkeleton } from '@/pages/Dashboard';
import { ClientSearch } from '@/pages/ClientSearch';
import { ClientProfile } from '@/pages/ClientProfile';
import { NewCheckup } from '@/pages/NewCheckup';
import { CheckupCompilation } from '@/pages/CheckupCompilation';
import { PridePreview } from '@/pages/PridePreview';
import { AdminCatalog } from '@/pages/AdminCatalog';
import { AdminPromotions } from '@/pages/AdminPromotions';
import { Notifications } from '@/pages/Notifications';
import { AdminUsers } from '@/pages/AdminUsers';
import { AdminMarketing } from '@/pages/AdminMarketing';
import { AdminQuestions } from '@/pages/AdminQuestions';
import { Revenue } from '@/pages/Revenue';
import { syncEngine } from '@/lib/sync';

/* ==========================================================================
   App — root component with routing, auth protection, and providers.
   ========================================================================== */

export function App() {
  // Register Service Worker on mount
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <OnlineProvider>
          <ToastProvider>
            <Routes>
              {/* Public */}
              <Route path="/login" element={<PublicRoute />}>
                <Route index element={<Login />} />
              </Route>

              {/* Protected */}
              <Route path="/" element={<ProtectedRoute />}>
                <Route index element={<Dashboard />} />
                <Route path="search" element={<ClientSearch />} />
                <Route path="clients/:id" element={<ClientProfile />} />
                <Route path="clients/:clientId/new-checkup" element={<NewCheckup />} />
                <Route path="checkups/:id" element={<CheckupCompilation />} />
                <Route path="checkups/:checkupId/pride" element={<PridePreview />} />
                <Route path="pride/:id" element={<PridePreview />} />
                <Route path="notifications" element={<Notifications />} />

                {/* Admin-only routes */}
                <Route element={<AdminRoute />}>
                  <Route path="admin/catalog" element={<AdminCatalog />} />
                  <Route path="admin/promotions" element={<AdminPromotions />} />
                  <Route path="admin/users" element={<AdminUsers />} />
                  <Route path="admin/marketing" element={<AdminMarketing />} />
                  <Route path="admin/fatturato" element={<Revenue />} />
                  <Route path="admin/questions" element={<AdminQuestions />} />
                </Route>
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ToastProvider>
        </OnlineProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

/* --------------------------------------------------------------------------
   Service Worker registration
   -------------------------------------------------------------------------- */

function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  // Only register in production (Vite dev server handles SW differently)
  if (import.meta.env.DEV) return;

  // Quando il nuovo SW prende controllo, ricarica una sola volta per smettere
  // di servire bundle vecchi dalla pagina già aperta. Senza questo l'utente
  // resta su asset stantii fino a refresh manuale.
  let isReloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (isReloading) return;
    isReloading = true;
    window.location.reload();
  });

  navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then((registration) => {
      // Forza check update appena registrato — utile dopo bump versione
      void registration.update();

      // Re-check quando l'utente torna sull'app (tab focus o ritorno da bg)
      const recheck = () => {
        if (document.visibilityState === 'visible') {
          void registration.update();
        }
      };
      document.addEventListener('visibilitychange', recheck);
      window.addEventListener('focus', recheck);

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (
            newWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            // New version available — activate immediately (poi controllerchange ricarica)
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });
    })
    .catch(() => {
      // SW registration failed — app works fine without it
    });

  // Listen for messages from the service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data as { type?: string } | undefined;
    if (data?.type === 'SYNC_REQUESTED') {
      // Service worker requested a sync — trigger the sync engine
      void syncEngine.processAll();
    }
  });
}

/* --------------------------------------------------------------------------
   Route guards
   -------------------------------------------------------------------------- */

/**
 * PublicRoute — only accessible when NOT authenticated.
 * Redirects to home if user is already logged in.
 */
function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <Outlet />;
}

/**
 * ProtectedRoute — requires authentication.
 * Shows LockScreen overlay when session is locked.
 *
 * Al boot da localStorage l'app parte locked: l'Outlet viene montato solo
 * dopo il primo PIN unlock, cosi' le pagine fanno il loro fetch iniziale
 * con il token fresco rigenerato dal verify. Dopo il primo unlock l'Outlet
 * resta montato anche se la sessione si lock-a di nuovo per timeout, per
 * non perdere lo stato delle pagine in compilazione.
 */
function ProtectedRoute() {
  const { isAuthenticated, isLocked, isLoading } = useAuth();
  const [hasBeenUnlocked, setHasBeenUnlocked] = useState(false);

  useEffect(() => {
    if (!isLocked) setHasBeenUnlocked(true);
  }, [isLocked]);

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <>
      {hasBeenUnlocked && <Outlet />}
      {isLocked && <LockScreen />}
    </>
  );
}

/**
 * AdminRoute — requires admin or super_admin role.
 * Redirects to home if user does not have sufficient permissions.
 */
function AdminRoute() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  if (!isAdmin) return <Navigate to="/" replace />;

  return <Outlet />;
}

/**
 * Minimal loading screen shown during initial boot / auth check.
 */
function LoadingScreen() {
  return <DashboardSkeleton />;
}
